import React from 'react';

type Language = 'en' | 'es';

interface Translations {
  title: string;
  content: string;
  backButton: string;
}

interface TranslationsMap {
  en: Translations;
  es: Translations;
}

const translations: TranslationsMap = {
  en: {
    title: 'About the Survey',
    content: `Hi there,

We are a team of curious developers who care about the future of environment management and the realities behind day-to-day delivery. Like many people working across modern web projects, we spend a lot of time thinking about the parts of the work that quietly shape everything else: setup, synchronization, deployments, tooling choices, and the operational habits that keep projects moving. This survey comes from that curiosity and from a genuine interest in understanding how teams are working today.

The way teams manage environments says a lot about how web work is evolving. New tooling, changing team structures, managed platforms, self-hosted approaches, and AI-assisted workflows are all influencing how projects move from idea to production. Beyond the hype, we are interested in the practical side of that shift: what still works well, what is becoming harder, and where current workflows continue to create friction.

This survey looks at that experience across projects where Drupal is part of the picture, whether in more traditional setups, headless architectures, or alongside broader frontend and infrastructure workflows. From local setup and configuration to content parity, deployments, support, and collaboration across production and non-production environments, we want to better understand how teams are working today and what patterns are starting to emerge.

By sharing your experience, you help build a clearer view of the current landscape, the trade-offs teams are making, and the challenges that still deserve more attention. If this is part of your world, we would be glad to hear your perspective.

With curiosity, coffee (it could be beer), and a soft spot for reliable deploys.

The Andor Cloud Team`,
    backButton: 'Back to Survey',
  },
  es: {
    title: 'Sobre la Encuesta',
    content: `Hola,

Somos un equipo de desarrolladores curiosos que nos preocupamos por el futuro de la gestion de entornos y las realidades detras del trabajo diario en equipo. Como desarrolladores que trabajamos en proyectos web modernos, pasamos mucho tiempo pensando en las partes del trabajo que silenciosamente dan forma a todo lo demas: configuracion, sincronizacion, despliegues, decisiones de herramientas y habitos operativos que mantienen los proyectos en movimiento. Esta encuesta surge de esa curiosidad y de un interes genuino por comprender como trabajan los equipos hoy.

La forma en que los equipos gestionan los entornos dice mucho sobre como esta evolucionando el trabajo web. Nuevas herramientas, estructuras de equipos cambiantes, plataformas gestionadas, enfoques autoalojados y flujos de trabajo asistidos por IA estan influyendo en como los proyectos pasan de la idea a la produccion. Mas alla del bombo, nos interesa el lado practico de ese cambio: lo que sigue funcionando bien, lo que se esta volviendo mas dificil y donde los flujos de trabajo actuales siguen creando fricción.

Esta encuesta analiza esa experiencia en proyectos donde Drupal es parte del panorama, ya sea en configuraciones mas tradicionales, arquitecturas headless o junto con flujos de trabajo mas amplios de frontend e infraestructura. Desde la configuracion local hasta la paridad de contenido, despliegues, soporte y colaboracion entre entornos de produccion y no produccion, queremos comprender mejor como trabajan los equipos hoy y que patrones estan comenzando a surgir.

Al compartir tu experiencia, nos ayudas a construir una vision más clara del panorama actual, las decisiones que los equipos estan tomando y los desafios que todavía merecen más atención. Nos encantaría escuchar tu perspectiva.

Con curiosidad, cafe (podria ser cerveza) y una debilidad por los despliegues confiables.

Equipo Andor Cloud`,
    backButton: 'Volver a la Encuesta',
  },
};

interface AboutUsPageProps {
  language: Language;
  onBack: () => void;
}

function AboutUsPage({ language, onBack }: AboutUsPageProps): React.ReactElement {
  const t = translations[language];

  return (
    <div className="about-us-page">
      <div className="about-us-page__content">
        <h1 className="about-us-page__title">{t.title}</h1>
        <div className="about-us-page__text">
          {t.content.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
        <button className="about-us-page__button" onClick={onBack}>
          {t.backButton}
        </button>
      </div>
    </div>
  );
}

export default AboutUsPage;
